import {
  FormatacaoConfigForm,
  FormatacaoConfiguracoes,
  FormatacaoRegra,
  FormatacaoRegraForm,
  ModoArredondamento,
  TipoCampoFormatacao
} from '../../../types/smartinteg';

export const TIPOS_CAMPO: { label: string; value: TipoCampoFormatacao }[] = [
  { label: 'Data', value: 'DATA' },
  { label: 'Data e hora', value: 'DATA_HORA' },
  { label: 'Número', value: 'NUMERO' },
  { label: 'Decimal', value: 'DECIMAL' },
  { label: 'Texto (não altera o valor)', value: 'TEXTO' }
];

export const ARREDONDAMENTOS: { label: string; value: ModoArredondamento }[] = [
  { label: 'Meio para cima (padrão)', value: 'HALF_UP' },
  { label: 'Meio para baixo', value: 'HALF_DOWN' },
  { label: 'Meio para o par', value: 'HALF_EVEN' },
  { label: 'Para cima (afasta do zero)', value: 'UP' },
  { label: 'Para baixo (em direção ao zero)', value: 'DOWN' },
  { label: 'Teto (infinito positivo)', value: 'CEILING' },
  { label: 'Piso (infinito negativo)', value: 'FLOOR' },
  { label: 'Exato (erro se precisar arredondar)', value: 'UNNECESSARY' }
];

// Padrões comuns de DateTimeFormatter (o campo aceita qualquer outro)
export const PADROES_DATA = [
  "yyyy-MM-dd",
  "yyyy-MM-dd'T'HH:mm:ss",
  "yyyy-MM-dd'T'HH:mm:ssZ",
  "yyyy-MM-dd HH:mm:ss",
  "dd/MM/yyyy",
  "dd/MM/yyyy HH:mm:ss",
  "yyyyMMdd"
];

export const PADRAO_DESTINO_DATA = "yyyy-MM-dd'T'HH:mm:ss";

export const ehData = (tipo: TipoCampoFormatacao) => tipo === 'DATA' || tipo === 'DATA_HORA';
export const ehNumero = (tipo: TipoCampoFormatacao) => tipo === 'NUMERO' || tipo === 'DECIMAL';

export function novaRegra(): FormatacaoRegraForm {
  return { campo: '', nomeDestino: '', tipo: 'DATA_HORA', formatoOrigem: '', formatoDestino: '', horaFixa: '', casasDecimais: null, arredondamento: 'HALF_UP' };
}

export function novoFormatacaoForm(): FormatacaoConfigForm {
  return { regras: [novaRegra()] };
}

/** Converte o LocalTime devolvido pelo backend para o texto editável sem perder frações de segundo. */
export function horaFixaParaFormulario(valor: FormatacaoRegra['horaFixa']): string {
  if (typeof valor === 'string') return /^\d{2}:\d{2}$/.test(valor) ? `${valor}:00` : valor;
  if (!Array.isArray(valor) || valor.length < 2 || valor.length > 4) return '';
  const [hora, minuto, segundo = 0, nanos = 0] = valor;
  if (![hora, minuto, segundo, nanos].every(Number.isInteger)
    || hora < 0 || hora > 23 || minuto < 0 || minuto > 59 || segundo < 0 || segundo > 59 || nanos < 0 || nanos > 999999999) return '';
  const base = [hora, minuto, segundo].map(n => String(n).padStart(2, '0')).join(':');
  return nanos ? `${base}.${String(nanos).padStart(9, '0').replace(/0+$/, '')}` : base;
}

/** Lê Recurso.configuracoes.formatacoes (aninhado) para o formulário */
export function lerFormatacao(cfg: Partial<FormatacaoConfiguracoes['formatacoes']> | null | undefined): FormatacaoConfigForm {
  const regras = cfg?.formatacoes;
  if (!Array.isArray(regras)) return novoFormatacaoForm();
  return {
    regras: regras.map(r => ({
      ...novaRegra(),
      campo: r.campo ?? '',
      nomeDestino: r.nomeDestino ?? '',
      tipo: r.tipo,
      formatoOrigem: r.formatoOrigem ?? '',
      formatoDestino: r.formatoDestino ?? '',
      horaFixa: horaFixaParaFormulario(r.horaFixa),
      casasDecimais: r.casasDecimais ?? null,
      arredondamento: r.arredondamento ?? 'HALF_UP'
    }))
  };
}

/** Monta o valor de Recurso.configuracoes.formatacoes com o aninhamento duplo; cada regra leva só o que o seu tipo usa */
export function montarFormatacao(form: FormatacaoConfigForm): FormatacaoConfiguracoes['formatacoes'] {
  const regras: FormatacaoRegra[] = form.regras.map(r => {
    const saida: FormatacaoRegra = { campo: r.campo.trim(), tipo: r.tipo };
    if (r.nomeDestino.trim()) saida.nomeDestino = r.nomeDestino.trim();
    if (ehData(r.tipo)) {
      if (r.formatoOrigem.trim()) saida.formatoOrigem = r.formatoOrigem.trim();
      if (r.formatoDestino.trim()) saida.formatoDestino = r.formatoDestino.trim();
      if (r.horaFixa.trim()) saida.horaFixa = r.horaFixa.trim();
    } else if (ehNumero(r.tipo) && r.casasDecimais !== null && r.casasDecimais !== undefined) {
      saida.casasDecimais = r.casasDecimais;
      saida.arredondamento = r.arredondamento;
    }
    return saida;
  });
  return { formatacoes: regras };
}

/** Destino efetivo da regra: o nome de destino ou o próprio campo */
export function destinoDaRegra(r: FormatacaoRegraForm): string {
  return r.nomeDestino.trim() || r.campo.trim();
}

const HORA_FIXA = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?$/;

/** Primeiro problema do formulário, ou null se está pronto para salvar */
export function validarFormatacao(form: FormatacaoConfigForm): string | null {
  if (form.regras.length === 0) return 'Informe ao menos uma regra de formatação.';
  const destinos = new Map<string, number>();
  for (const [i, r] of form.regras.entries()) {
    const onde = `Regra ${i + 1}`;
    if (!r.campo.trim()) return `${onde}: informe o campo.`;
    if (ehData(r.tipo) && r.horaFixa.trim() && !HORA_FIXA.test(r.horaFixa.trim())) return `${onde}: a hora fixa deve estar no formato HH:mm:ss (ex.: 07:00:00).`;
    if (ehNumero(r.tipo) && r.casasDecimais !== null && r.casasDecimais !== undefined && (!Number.isInteger(r.casasDecimais) || r.casasDecimais < 0 || r.casasDecimais > 18)) {
      return `${onde}: as casas decimais devem ser um inteiro de 0 a 18.`;
    }
    const destino = destinoDaRegra(r).toLowerCase();
    if (destinos.has(destino)) return `${onde}: o campo de destino "${destinoDaRegra(r)}" já é gravado pela regra ${destinos.get(destino)}.`;
    destinos.set(destino, i + 1);
  }
  return null;
}
